import { Box, Typography, Avatar, AvatarGroup } from "@mui/material";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import LinearProgressBar from "../../../Components/LinearProgressBar";

const CARD_BORDER = "1px solid rgba(234, 234, 234, 1)";
const TEXT_MAIN = "rgba(47, 43, 61, 0.9)";
const TEXT_SUB = "rgba(71, 84, 103, 1)";
const GREEN = "rgba(36, 139, 112, 1)";
const AMBER = "rgba(180, 120, 20, 1)";
const RED = "rgba(200, 55, 55, 1)";
const PURPLE = "#7367F0";

const s = {
  card: {
    border: CARD_BORDER,
    borderRadius: "12px",
    backgroundColor: "#fff",
    padding: "18px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    height: "100%",
    overflow: "hidden",
  },
  cover: { margin: "-18px -18px 0", height: "84px", overflow: "hidden", backgroundColor: "rgba(242, 243, 251, 1)" },
  coverImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },

  topRow: { display: "flex", alignItems: "center", gap: "8px" },
  logo: { width: 44, height: 44, fontSize: "16px", bgcolor: "rgba(242, 243, 251, 1)", color: "rgba(62, 63, 76, 0.7)", border: "2px solid #fff", marginTop: "-30px", boxShadow: "0 1px 3px rgba(0,0,0,0.12)" },
  name: { flex: 1, minWidth: 0, fontSize: "17px", fontWeight: 600, color: TEXT_MAIN, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  openBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "30px",
    height: "30px",
    minWidth: "30px",
    borderRadius: "8px",
    border: CARD_BORDER,
    color: "rgba(47, 43, 61, 0.7)",
    textDecoration: "none",
    backgroundColor: "#fff",
    cursor: "pointer",
    padding: 0,
    flexShrink: 0,
    "&:hover": { backgroundColor: "rgba(219, 240, 236, 0.6)", color: GREEN, borderColor: "rgba(36, 139, 112, 0.4)" },
  },

  // Manager sits on the name's line (label stacked over the name) so it costs no
  // extra row; capped so a long manager name can't squeeze out the community's.
  manager: { display: "flex", alignItems: "center", gap: "6px", minWidth: 0, maxWidth: "42%", flexShrink: 0 },
  managerAvatar: { width: 24, height: 24, fontSize: "10px" },
  managerText: { display: "flex", flexDirection: "column", minWidth: 0 },
  managerLabel: { fontSize: "11px", color: TEXT_SUB, lineHeight: 1.2 },
  managerName: { fontSize: "13px", fontWeight: 600, color: TEXT_MAIN, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  managerYou: { fontWeight: 400, color: TEXT_SUB },

  peopleRow: { display: "flex", gap: "10px", flexWrap: "wrap" },
  people: { display: "flex", alignItems: "center", gap: "8px", flex: 1, minWidth: "130px" },
  avatarGroup: { "& .MuiAvatar-root": { width: 26, height: 26, fontSize: "11px", border: "2px solid #fff" } },
  peopleText: { display: "flex", flexDirection: "column", lineHeight: 1.15 },
  peopleCount: { fontSize: "15px", fontWeight: 600, color: TEXT_MAIN },
  peopleLabel: { fontSize: "12px", color: TEXT_SUB },

  moneyRow: { display: "flex", gap: "12px", borderTop: CARD_BORDER, borderBottom: CARD_BORDER, py: "12px" },
  money: { flex: 1, display: "flex", flexDirection: "column", gap: "2px" },
  moneyLabel: { fontSize: "12px", color: TEXT_SUB },
  moneyValueRow: { display: "flex", alignItems: "baseline", gap: "6px", flexWrap: "wrap" },
  moneyValue: { fontSize: "20px", fontWeight: 700, color: TEXT_MAIN, lineHeight: 1.1 },
  trend: { display: "inline-flex", alignItems: "center", fontSize: "12px", fontWeight: 600 },

  goalWrap: { display: "flex", flexDirection: "column", gap: "7px" },
  goalRow: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "8px" },
  goalLabel: { fontSize: "13px", color: TEXT_SUB },
  goalAmount: { fontSize: "13px", fontWeight: 600, color: TEXT_MAIN, whiteSpace: "nowrap" },
  projectsLine: { fontSize: "12px", color: TEXT_SUB },
  projectsStrong: { fontWeight: 600, color: PURPLE },

  needsWrap: { display: "flex", flexDirection: "column", gap: "8px" },
  needRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    width: "100%",
    padding: "10px 12px",
    borderRadius: "10px",
    border: CARD_BORDER,
    cursor: "pointer",
    backgroundColor: "#fff",
    fontFamily: "inherit",
    textAlign: "start",
    "&:hover": { backgroundColor: "rgba(245, 246, 250, 1)" },
  },
  needIcon: (color, bg) => ({ width: "30px", height: "30px", minWidth: "30px", borderRadius: "8px", backgroundColor: bg, color, display: "flex", alignItems: "center", justifyContent: "center" }),
  needText: { flex: 1, fontSize: "13.5px", fontWeight: 500, color: TEXT_MAIN, minWidth: 0 },
  needChevron: { color: "rgba(62, 63, 76, 0.4)", fontSize: 20 },
  caughtUp: { display: "flex", alignItems: "center", gap: "8px", padding: "10px 12px", borderRadius: "10px", backgroundColor: "rgba(219, 240, 236, 0.5)", fontSize: "13.5px", fontWeight: 500, color: GREEN },

  setupRow: { display: "flex", flexDirection: "column", gap: "5px" },
  setupLabel: { fontSize: "12px", color: TEXT_SUB },
};

const People = ({ avatars, count, label }) => (
  <Box sx={s.people}>
    <AvatarGroup max={4} sx={s.avatarGroup}>
      {avatars.map((src, i) => (
        <Avatar key={i} src={src} alt="" />
      ))}
    </AvatarGroup>
    <Box sx={s.peopleText}>
      <Typography sx={s.peopleCount}>{count}</Typography>
      <Typography sx={s.peopleLabel}>{label}</Typography>
    </Box>
  </Box>
);

// One community in the CM's multi-community overview: a cover banner for instant
// recognition, a diagonal arrow that opens the community in a new tab, avatar
// groups for members/suppliers, income (with a month-over-month trend) & profit,
// the monthly-goal + projects line, and the "needs your attention" rows. The
// rows show only on a community the viewer manages DIRECTLY — pending approvals
// are its CM's to act on, whatever other roles the viewer holds — and those
// cards get an amber accent when something is waiting.
const CommunityCard = ({ community, cover, loc, money, t, isRTL, onOpen }) => {
  const {
    members, suppliers, memberAvatars, supplierAvatars,
    revenueThisMonth, earningsThisMonth, income12mo = [],
    monthlyGoal, goalCollected, activeProjects, piggybankAvailable,
    needs, setupPercent, managedByMe, manager,
  } = community;
  const showGoal = monthlyGoal > 0 && goalCollected != null;
  const goalPct = showGoal ? Math.min((goalCollected / monthlyGoal) * 100, 100) : 0;
  const Chevron = isRTL ? ChevronLeftIcon : ChevronRightIcon;

  const pendingCount = (needs.joinRequests || 0) + (needs.supplierOffers || 0);
  const hasNeeds = managedByMe && pendingCount > 0;

  const prevIncome = income12mo[income12mo.length - 2] ?? 0;
  const trendPct =
    prevIncome > 0 && revenueThisMonth > 0
      ? Math.round(((income12mo[income12mo.length - 1] - prevIncome) / prevIncome) * 100)
      : null;
  const trendUp = trendPct != null && trendPct >= 0;

  const needItems = [
    needs.joinRequests > 0
      ? { key: "joinRequests", label: t("needs.joinRequests", { count: needs.joinRequests }), icon: <PersonAddAlt1OutlinedIcon sx={{ fontSize: 18 }} />, color: AMBER, bg: "rgba(255, 244, 229, 1)", target: "members" }
      : null,
    needs.supplierOffers > 0
      ? { key: "supplierOffers", label: t("needs.supplierOffers", { count: needs.supplierOffers }), icon: <StorefrontOutlinedIcon sx={{ fontSize: 18 }} />, color: PURPLE, bg: "rgba(242, 243, 251, 1)", target: "suppliers" }
      : null,
  ].filter(Boolean);

  return (
    <Box sx={{ ...s.card, ...(hasNeeds ? { border: "1px solid rgba(180, 120, 20, 0.5)", boxShadow: "0 0 0 3px rgba(180, 120, 20, 0.08)" } : {}) }}>
      {/* The banner band always renders — the logo overlaps it. Communities
          have no dedicated banner field yet, so it shows the community's own
          image when it has one and a plain tint otherwise. */}
      <Box sx={s.cover}>
        {cover && <Box component="img" src={cover} alt="" sx={s.coverImg} />}
      </Box>

      <Box sx={s.topRow}>
        <Avatar src={community.image} alt={loc(community.name)} sx={s.logo}>
          {loc(community.name).charAt(0)}
        </Avatar>
        <Typography sx={s.name}>{loc(community.name)}</Typography>
        {/* Who runs this community — on the name's line, beside the open arrow.
            The viewer's own name marks the ones they manage directly, which is
            also why only those cards carry pending items. */}
        {manager && (
          <Box sx={s.manager}>
            <Avatar src={manager.avatar} alt={loc(manager.name)} sx={s.managerAvatar}>
              {loc(manager.name).charAt(0)}
            </Avatar>
            <Box sx={s.managerText}>
              <Typography sx={s.managerLabel}>{t("card.manager")}</Typography>
              <Typography sx={s.managerName}>
                {loc(manager.name)}
                {managedByMe && (
                  <Box component="span" sx={s.managerYou}>
                    {" "}
                    {t("card.you")}
                  </Box>
                )}
              </Typography>
            </Box>
          </Box>
        )}
        {/* Opens this community's dashboard (switching to it first). Opening a
            community means working in it as its manager, so the arrow is only
            on communities the viewer manages directly — an MCM-only ("MoM")
            community has nothing for the viewer to open. */}
        {managedByMe && (
          <Box component="button" type="button" sx={s.openBtn} aria-label={t("card.open")} onClick={(e) => { e.stopPropagation(); onOpen?.(community, "dashboard"); }}>
            <NorthEastIcon sx={{ fontSize: 17 }} />
          </Box>
        )}
      </Box>

      <Box sx={s.peopleRow}>
        <People avatars={memberAvatars} count={members.current} label={t("card.members")} />
        <People avatars={supplierAvatars} count={suppliers} label={t("card.suppliers")} />
      </Box>

      <Box sx={s.moneyRow}>
        <Box sx={s.money}>
          <Typography sx={s.moneyLabel}>{t("card.income")}</Typography>
          <Box sx={s.moneyValueRow}>
            <Typography sx={s.moneyValue}>{money(revenueThisMonth)}</Typography>
            {trendPct != null && (
              <Box component="span" sx={{ ...s.trend, color: trendUp ? GREEN : RED }}>
                {trendUp ? <ArrowDropUpIcon sx={{ fontSize: 18 }} /> : <ArrowDropDownIcon sx={{ fontSize: 18 }} />}
                {Math.abs(trendPct)}%
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={s.money}>
          <Typography sx={s.moneyLabel}>{t("card.profit")}</Typography>
          <Typography sx={{ ...s.moneyValue, color: GREEN }}>{money(earningsThisMonth)}</Typography>
        </Box>
      </Box>

      {showGoal && (
        <Box sx={s.goalWrap}>
          <Box sx={s.goalRow}>
            <Typography sx={s.goalLabel}>{t("card.goal")}</Typography>
            <Typography sx={s.goalAmount}>
              {money(goalCollected)} / {money(monthlyGoal)}
            </Typography>
          </Box>
          <LinearProgressBar percentage={goalPct} showPercentageText={false} />
          <Typography sx={s.projectsLine}>
            <Box component="span" sx={s.projectsStrong}>
              {t("card.projects", { count: activeProjects })}
            </Box>
            {" · "}
            {t("card.available", { amount: money(piggybankAvailable) })}
          </Typography>
        </Box>
      )}

      {managedByMe && (
        <Box sx={s.needsWrap}>
          {needItems.length > 0 ? (
            needItems.map((nItem) => (
              <Box key={nItem.key} component="button" type="button" onClick={() => onOpen?.(community, nItem.target)} sx={s.needRow}>
                <Box sx={s.needIcon(nItem.color, nItem.bg)}>{nItem.icon}</Box>
                <Typography sx={s.needText}>{nItem.label}</Typography>
                <Chevron sx={s.needChevron} />
              </Box>
            ))
          ) : (
            <Box sx={s.caughtUp}>
              <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
              {t("card.caught_up")}
            </Box>
          )}
        </Box>
      )}

      {setupPercent != null && setupPercent < 100 && (
        <Box sx={s.setupRow}>
          <Typography sx={s.setupLabel}>{t("card.setup", { percent: setupPercent })}</Typography>
          <LinearProgressBar percentage={setupPercent} showPercentageText={false} />
        </Box>
      )}
    </Box>
  );
};

export default CommunityCard;
